/**
 * Company: SPHMedia
 * Description: This is the component responsible for infinite scrolling of content-hub stories
 */

import { TeaserDataApiResponse } from "@app/types/ServerCustomContext";
import { mapTeaserDataToLatestProps } from "@app/utils/mapTeaserDataToProps";
import { useInView } from "@components/hooks/useInView";
import {
  configSite,
  configTrackingCode,
  CustomListingLayoutProps,
} from "@pub/config";
import { Loader } from "@sphtech/dmg-design-system";
import { LOAD_MORE_TEXT } from "@src/pubs/common/config";
import { Fragment, ReactElement, ReactNode, useEffect } from "react";
import useSWRInfinite from "swr/infinite";

import ContentHubStoryListingLayout from "./ContentHubStoryListingLayout";

type Props = {
  data: {
    tagSlugs: string;
    limit: number;
    ignoreIds: string[];
    lb1?: ReactNode;
  };
  CustomListingLayout?: CustomListingLayoutProps;
};

/**
 * ContentHubStoryListing
 * Description: This is the component responsible for infinite scrolling of content-hub stories
 * @param props - The props required by the component
 * @returns The rendered content-hub stories
 */
export default function ContentHubStoryListing({
  data,
  CustomListingLayout,
}: Props): ReactElement {
  const { ref, inView } = useInView({ threshold: 0.1 });
  const limit = data.limit;

  const getKey = (pageIndex: number) => {
    return `/_plat/api/teaser/latest?${[
      `tags=${encodeURIComponent(data.tagSlugs)}`,
      `ignoreIds=${encodeURIComponent(data.ignoreIds.join(","))}`,
      `from=${pageIndex * limit}`,
      `size=${limit}`,
    ].join("&")}`;
  };

  const {
    data: moreData,
    isLoading,
    size,
    setSize,
  } = useSWRInfinite<TeaserDataApiResponse>(getKey, {
    revalidateFirstPage: false,
    initialSize: 0,
  });

  const sizeLength = size - 1;
  const isLoadingMore =
    isLoading ||
    (size > 0 && moreData && typeof moreData[sizeLength] === "undefined");

  const hasMoreData = size === 0 || !!moreData?.[size - 1]?.results?.length;

  useEffect(() => {
    if (!inView || !hasMoreData) return;
    void setSize((prev) => {
      window.SPHMGTM.cmd.push(`pagination=${prev + 1}`);
      return prev + 1;
    });
  }, [inView, setSize, hasMoreData]);

  return (
    <>
      {moreData &&
        moreData.length > 0 &&
        moreData.map((el, index) => {
          if (!el?.results?.length) {
            return;
          }

          const elem = el.results.map((teaser) =>
            mapTeaserDataToLatestProps(
              teaser,
              configTrackingCode.contenthub?.listing,
            ),
          );

          return (
            <Fragment key={index}>
              {data.lb1}
              {CustomListingLayout ? (
                <CustomListingLayout
                  isListing={true}
                  data={{ listedStories: elem }}
                />
              ) : (
                <ContentHubStoryListingLayout
                  data={{
                    listedStories: elem,
                  }}
                />
              )}
            </Fragment>
          );
        })}
      <div ref={ref}>
        {isLoadingMore && (
          <Loader
            label={configSite.loadMore?.text || LOAD_MORE_TEXT}
            variant={configSite.loadMore?.variant}
          />
        )}
      </div>
    </>
  );
}
